import { PaymentPlan, PaymentPlanFeatures, PlanFormValues } from 'utils/types';

export type PlanModalState = { mode: 'create' } | { mode: 'edit'; plan: PaymentPlan };

const DEFAULT_FEATURES: PaymentPlanFeatures = {
  activityFeed: true,
  schedule: true,
  selections: true,
  fileCabinet: true,
  finance: false,
};

const centsToDollars = (cents: number): number => cents / 100;

const defaultTrialDaysForRecurring = (recurring: PaymentPlan['recurring'] = 'MONTHLY') =>
  recurring === 'MONTHLY' ? 14 : 0;

export const getPlanFormDefaultValues = (modalState: PlanModalState): PlanFormValues => {
  if (modalState.mode === 'create') {
    return {
      name: '',
      recurring: 'MONTHLY',
      trialDays: 14,
      features: { ...DEFAULT_FEATURES },
      isVisible: true,
    };
  }

  const { plan } = modalState;

  return {
    id: plan.id,
    name: plan.name,
    recurring: plan.recurring,
    externalProductId: plan.externalProductId,
    ofProjects: plan.data?.maxProjectsAmount ?? '',
    ofUsers: plan.data?.maxUsersAmount ?? '',
    storageLimit: plan.data?.storageLimit ?? '',
    amount: centsToDollars(plan.amount),
    trialDays: plan.trialDays ?? defaultTrialDaysForRecurring(plan.recurring),
    isVisible: !plan.hidden,
    features: plan.features ?? { ...DEFAULT_FEATURES },
  };
};

export type PlanUpsertPayload = {
  name: string;
  amount: number;
  recurring: PaymentPlan['recurring'];
  trialDays: number;
  hidden: boolean;
  data: {
    maxProjectsAmount: number | null;
    maxUsersAmount: number | null;
    storageLimit: number | null;
  };
  features: PaymentPlanFeatures;
};

export type PlanCreatePayload = PlanUpsertPayload & {
  externalProductId: number;
};

const normalizeLimit = (value: number | '' | null | undefined): number | null => {
  if (value === '' || value == null) {
    return null;
  }

  return Number(value);
};

const mapSharedPayload = (values: PlanFormValues): PlanUpsertPayload => ({
  name: values.name,
  amount: Math.round(Number(values.amount) * 100),
  recurring: values.recurring as PaymentPlan['recurring'],
  trialDays: Number(values.trialDays ?? 0),
  hidden: values.isVisible === false,
  data: {
    maxProjectsAmount: normalizeLimit(values.ofProjects),
    maxUsersAmount: normalizeLimit(values.ofUsers),
    storageLimit: normalizeLimit(values.storageLimit),
  },
  features: values.features ?? DEFAULT_FEATURES,
});

export const mapFormValuesToUpdatePayload = (values: PlanFormValues): PlanUpsertPayload =>
  mapSharedPayload(values);

export const mapFormValuesToCreatePayload = (values: PlanFormValues): PlanCreatePayload => ({
  ...mapSharedPayload(values),
  externalProductId: Number(values.externalProductId),
});
